import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getDefaultRoute } from '../utils/getDefaultRoute';
import LoginPage from '../pages/common/LoginPage';
import ErrorPage from '../pages/common/ErrorPage';
import AccessDenied from '../pages/common/AccessDenied';
import UnderConstruction from '../pages/common/UnderConstruction';
import AdminLayout from '../layouts/AdminLayout';
import FacultyLayout from '../layouts/FacultyLayout';
import StudentLayout from '../layouts/StudentLayout';
import HodLayout from '../layouts/HodLayout';
import AssessmentContainerLayout from '../layouts/AssessmentContainerLayout';
import {
  RequireAuth,
  RequireFacultyTeaching,
  RequireCbcFaculty,
  RequireFacultyCoding,
  RequirePlatformAnalyticsApi,
  ProtectedRoute,
  LegacyBatchRedirect,
  LegacyProjectDetailRedirect,
  LegacyProjectEvaluateRedirect,
  LegacyCodingTrackerRedirect,
  LegacyProfileRedirect,
  LegacyCoursesPathRedirect,
  LegacyPlagiarismRedirect,
  LegacyCodingOverviewRedirect,
  LegacyCodingActivityRedirect,
  LegacyFacultyCodingAnalyticsRedirect,
  LegacyRoleHomeRedirect,
  RoleBasedProjectRedirect,
  RequirePlacementPermission,
  RequireStudentEligibility,
} from './routeGuards';
import { RequireProblemIntelligenceRead } from './RequireProblemIntelligenceRead';
import { RequireAssessmentOrchestration } from './RequireAssessmentOrchestration';
import { RequireReportingIntelligence } from './RequireReportingIntelligence';
import {
  placementCampaignPath,
  placementInterviewPath,
} from '../utils/placementPaths';
import {
  canViewPlacementOverview,
  canManageMockCampaigns,
  canConductMockInterview,
  canViewCoachingInsights,
  canViewCoachingStudents,
  canViewPlacementBatchAnalytics,
  canCurateCompanyIntel,
  isStudentPlacementUser,
} from '../utils/placementPermissions';

function LegacyPlacementDriveRedirect() {
  const { driveId } = useParams();
  const { user } = useAuth();
  return <Navigate to={placementCampaignPath(user, driveId)} replace />;
}

function LegacyPlacementCampaignRedirect() {
  const { campaignId, driveId } = useParams();
  const { user } = useAuth();
  return <Navigate to={placementCampaignPath(user, campaignId || driveId)} replace />;
}

function LegacyPlacementInterviewRedirect() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  return <Navigate to={placementInterviewPath(user, sessionId)} replace />;
}

function RouteFallback() {
  return (
    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function withSuspense(importFn) {
  const LazyComponent = lazy(importFn);
  return function SuspendedRoute(props) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

/* Public legal documents (AD-LP-1) — no auth, no layout, no guard. */
const PrivacyPolicyPage = withSuspense(() => import('../pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = withSuspense(() => import('../pages/legal/TermsOfServicePage'));
const AccountLinkingPage = withSuspense(() => import('../pages/legal/AccountLinkingPage'));

const Profile = withSuspense(() => import('../pages/common/Profile'));
const HodInterventionDashboard = withSuspense(() => import('../pages/hod/HodInterventionDashboard'));
const HodDashboard = withSuspense(() => import('../pages/hod/HodDashboard'));
const HodStudents = withSuspense(() => import('../pages/hod/HodStudents'));
const HodStudent360 = withSuspense(() => import('../pages/hod/HodStudent360'));
const HodBatches = withSuspense(() => import('../pages/hod/HodBatches'));
const HodBatchCoding = withSuspense(() => import('../pages/hod/HodBatchCoding'));
const HodFaculty = withSuspense(() => import('../pages/hod/HodFaculty'));
const HodMockInterviews = withSuspense(() => import('../pages/hod/HodMockInterviews'));
const HodDiaryMetrics = withSuspense(() => import('../pages/hod/HodDiaryMetrics'));
const AdminOverview = withSuspense(() => import('../pages/admin/AdminOverview'));
const AdminUsersPage = withSuspense(() => import('../pages/admin/AdminUsersPage'));
const AccessControlPage = withSuspense(() => import('../pages/admin/AccessControlPage'));
const CodingPlatformsPage = withSuspense(() => import('../pages/admin/CodingPlatformsPage'));
const PlatformHandleClaimsPage = withSuspense(() => import('../pages/admin/PlatformHandleClaimsPage'));
const PlatformHealthPage = withSuspense(() => import('../pages/admin/PlatformHealthPage'));
const SyncOperationsPage = withSuspense(() => import('../pages/admin/SyncOperationsPage'));
const DataIntegrityPage = withSuspense(() => import('../pages/admin/DataIntegrityPage'));
const StudentRecoveryPage = withSuspense(() => import('../pages/admin/StudentRecoveryPage'));
const RecoveryCenterPage = withSuspense(() => import('../pages/admin/RecoveryCenterPage'));
const AdminBackupsPage = withSuspense(() => import('../pages/admin/AdminBackupsPage'));
const FeatureManagementPage = withSuspense(() => import('../pages/admin/FeatureManagementPage'));
const MonitoringRulesPage = withSuspense(() => import('../pages/admin/MonitoringRulesPage'));
const AssessmentAnomaliesPage = withSuspense(() => import('../pages/admin/AssessmentAnomaliesPage'));
const MonitoringAlarmsPage = withSuspense(() => import('../pages/admin/MonitoringAlarmsPage'));
const MonitoringAnalyticsPage = withSuspense(() => import('../pages/admin/MonitoringAnalyticsPage'));
const MonitoringPurgePage = withSuspense(() => import('../pages/admin/MonitoringPurgePage'));
const ProblemIntelligenceOpsPage = withSuspense(() => import('../pages/admin/ProblemIntelligenceOpsPage'));
const PlacementDrivesPage = withSuspense(() => import('../pages/placement/PlacementDrivesPage'));
const PlacementDriveDetailPage = withSuspense(() => import('../pages/placement/PlacementDriveDetailPage'));
const PostDriveCapturePage = withSuspense(() => import('../pages/placement/PostDriveCapturePage'));
const PlacementOverviewPage = withSuspense(() => import('../pages/placement/PlacementOverviewPage'));
const AssignedInterviewsPage = withSuspense(() => import('../pages/placement/AssignedInterviewsPage'));
const InterviewScreen = withSuspense(() => import('../pages/placement/InterviewScreen'));
const FacultyAttentionPage = withSuspense(() => import('../pages/placement/FacultyAttentionPage'));
const CoordinatorCareerOverviewPage = withSuspense(() => import('../pages/placement/CoordinatorCareerOverviewPage'));
const StudentPlacementHubPage = withSuspense(() => import('../pages/placement/StudentPlacementHubPage'));
const StudentPlacementDashboard = withSuspense(() => import('../pages/student/StudentPlacementDashboard'));
const StudentCompanyMatchesPage = withSuspense(() => import('../pages/student/StudentCompanyMatchesPage'));
const StudentCompanyInsightPage = withSuspense(() => import('../pages/student/StudentCompanyInsightPage'));
const StudentPlacementProfilePage = withSuspense(() => import('../pages/placement/StudentPlacementProfilePage'));
const StudentInterviewHistoryPage = withSuspense(() => import('../pages/placement/StudentInterviewHistoryPage'));
const StudentCareerIntelligencePage = withSuspense(() => import('../pages/placement/StudentCareerIntelligencePage'));
const FacultyPreparationWorkspacePage = withSuspense(() => import('../pages/faculty/preparation/FacultyPreparationWorkspacePage'));
const BatchPlacementReadinessPage = withSuspense(() => import('../pages/faculty/BatchPlacementReadinessPage'));
const FacultyJourneyBatchPage = withSuspense(() => import('../pages/faculty/journey/FacultyJourneyBatchPage'));
const FacultyJourneyMonitorPage = withSuspense(() => import('../pages/faculty/journey/FacultyJourneyMonitorPage'));
const CompanyIntelligenceAdminPage = withSuspense(() => import('../pages/admin/CompanyIntelligenceAdminPage'));
const PlagiarismAnalyticsPage = withSuspense(() => import('../pages/common/PlagiarismAnalyticsPage'));
const FacultyWorkspaceBatchesPage = withSuspense(() => import('../pages/faculty/workspace/FacultyWorkspaceBatchesPage'));
const BatchListPage = withSuspense(() => import('../pages/coding-tracker/BatchListPage'));
const BatchDetailsPage = withSuspense(() => import('../pages/coding-tracker/BatchDetailsPage'));
const AssignmentPage = withSuspense(() => import('../pages/coding-tracker/AssignmentPage'));
const AssignmentDetailsPage = withSuspense(() => import('../pages/coding-tracker/AssignmentDetailsPage'));
const ProgressPage = withSuspense(() => import('../pages/coding-tracker/ProgressPage'));
const BatchStudentsTabRedirect = withSuspense(() => import('../pages/coding-tracker/BatchStudentsTabRedirect'));
const FacultyDashboard = withSuspense(() => import('../pages/faculty/FacultyDashboard'));
const FacultyProjectDashboard = withSuspense(() => import('../pages/FacultyProjectDashboard'));
const StudentProjectDashboard = withSuspense(() => import('../pages/StudentProjectDashboard'));
const MyCourses = withSuspense(() => import('../pages/faculty/MyCourses'));
const FacultyLabInternalsPage = withSuspense(() => import('../pages/faculty/FacultyLabInternalsPage'));
const CreateLabInternalPage = withSuspense(() => import('../pages/faculty/CreateLabInternalPage'));
const FacultyLabDetailPage = withSuspense(() => import('../pages/faculty/FacultyLabDetailPage'));
const FacultyStudentEvaluationPage = withSuspense(() => import('../pages/faculty/FacultyStudentEvaluationPage'));
const FacultyQuestionBankPage = withSuspense(() => import('../pages/faculty/FacultyQuestionBankPage'));
const QuestionFormPage = withSuspense(() => import('../pages/faculty/QuestionFormPage'));
const ProjectEvaluation = withSuspense(() => import('../pages/faculty/ProjectEvaluation'));
const ProjectDetails = withSuspense(() => import('../pages/common/ProjectDetails'));
const StudentDashboard = withSuspense(() => import('../pages/student/StudentDashboard'));
const StudentAssignmentsDashboard = withSuspense(() => import('../pages/student/assignments/StudentAssignmentsDashboard'));
const StudentAssignmentDetailPage = withSuspense(() => import('../pages/student/assignments/StudentAssignmentDetailPage'));
const StudentCodingDashboard = withSuspense(() => import('../pages/student/StudentCodingDashboard'));
const StudentProgressPage = withSuspense(() => import('../pages/student/ProgressPage'));
const PlatformProgressErrorShowcase = withSuspense(() => import('../pages/student/PlatformProgressErrorShowcase'));
const StudentLabDashboard = withSuspense(() => import('../pages/student/StudentLabDashboard'));
const StudentLabResultsPage = withSuspense(() => import('../pages/student/StudentLabResultsPage'));
const StudentLabResultDetailPage = withSuspense(() => import('../pages/student/StudentLabResultDetailPage'));
const StudentSubmissionResultPage = withSuspense(() => import('../pages/student/StudentSubmissionResultPage'));
const StudentIDE = withSuspense(() => import('../pages/student/StudentIDE'));
const FacultyLabResultsPage = withSuspense(() => import('../pages/faculty/FacultyLabResultsPage'));
const FacultyLabSubmissionDetailPage = withSuspense(() => import('../pages/faculty/FacultyLabSubmissionDetailPage'));
const FacultyStudentsPage = withSuspense(() => import('../pages/faculty/FacultyStudentsPage'));
const FacultyActivityFeedPage = withSuspense(() => import('../pages/faculty/FacultyActivityFeedPage'));
const FacultyStudentPortfolioPage = withSuspense(() => import('../pages/faculty/FacultyStudentPortfolioPage'));
const DiaryDashboardPage = withSuspense(() => import('../pages/faculty/diary/DiaryDashboardPage'));
const DiaryDayPage = withSuspense(() => import('../pages/faculty/diary/DiaryDayPage'));
const DiaryComposePage = withSuspense(() => import('../pages/faculty/diary/DiaryComposePage'));
const DiaryEntryDetailPage = withSuspense(() => import('../pages/faculty/diary/DiaryEntryDetailPage'));
const LessonPlanListPage = withSuspense(() => import('../pages/faculty/lessonPlans/LessonPlanListPage'));
const LessonPlanFormPage = withSuspense(() => import('../pages/faculty/lessonPlans/LessonPlanFormPage'));
const LessonPlanImportPage = withSuspense(() => import('../pages/faculty/lessonPlans/LessonPlanImportPage'));
const LessonPlanDetailPage = withSuspense(() => import('../pages/faculty/lessonPlans/LessonPlanDetailPage'));
const LessonPlanSessionsPage = withSuspense(() => import('../pages/faculty/lessonPlans/LessonPlanSessionsPage'));
const TeachingReportsPage = withSuspense(() => import('../pages/faculty/teachingReports/TeachingReportsPage'));
const AdminTeachingReportsPage = withSuspense(() => import('../pages/admin/AdminTeachingReportsPage'));
const DiaryTransferPage = withSuspense(() => import('../pages/admin/DiaryTransferPage'));
const ProblemLibraryListPage = withSuspense(() => import('../pages/problemLibrary/ProblemLibraryListPage'));
const ProblemLibraryDetailPage = withSuspense(() => import('../pages/problemLibrary/ProblemLibraryDetailPage'));
const ProblemLibraryAnalyticsPage = withSuspense(() => import('../pages/problemLibrary/ProblemLibraryAnalyticsPage'));
const SessionLibraryPage = withSuspense(() => import('../pages/faculty/sessionLibrary/SessionLibraryPage'));
const SessionTemplateFormPage = withSuspense(() => import('../pages/faculty/sessionLibrary/SessionTemplateFormPage'));
const SessionTemplateDetailPage = withSuspense(() => import('../pages/faculty/sessionLibrary/SessionTemplateDetailPage'));
const AssessmentListPage = withSuspense(() => import('../pages/faculty/assessments/AssessmentListPage'));
const AssessmentWizardPage = withSuspense(() => import('../pages/faculty/assessments/AssessmentWizardPage'));
const LiveMonitorPage = withSuspense(() => import('../pages/faculty/assessments/LiveMonitorPage'));
const AssessmentSummaryPage = withSuspense(() => import('../pages/faculty/assessments/AssessmentSummaryPage'));
const AssessmentDetailPage = withSuspense(() => import('../pages/faculty/assessments/AssessmentDetailPage'));
const ReviewQueuePage = withSuspense(() => import('../pages/faculty/assessments/ReviewQueuePage'));
const IntegrityDashboardPage = withSuspense(
  () => import('../pages/faculty/assessments/IntegrityDashboardPage')
);
const IntegrityReportPage = withSuspense(
  () => import('../pages/faculty/assessments/IntegrityReportPage')
);
const AttemptDetailPage = withSuspense(() => import('../pages/faculty/assessments/AttemptDetailPage'));
const StudentAssessmentListPage = withSuspense(() => import('../pages/student/assessments/StudentAssessmentListPage'));
const AssessmentIntroPage = withSuspense(() => import('../pages/student/assessments/AssessmentIntroPage'));
const AssessmentLivePage = withSuspense(() => import('../pages/student/assessments/AssessmentLivePage'));
const AssessmentCompletePage = withSuspense(() => import('../pages/student/assessments/AssessmentCompletePage'));
const AssessmentResultsPage = withSuspense(() => import('../pages/student/assessments/AssessmentResultsPage'));
const CodingAnalyticsShell = withSuspense(() => import('../components/coding-analytics/CodingAnalyticsShell'));
const CodingWorkspaceShell = withSuspense(() => import('../components/coding-workspace/CodingWorkspaceShell'));
const FacultyWorkspaceOverview = withSuspense(() => import('../pages/faculty/workspace/FacultyWorkspaceOverview'));
const FacultyWorkspaceAssignments = withSuspense(() => import('../pages/faculty/workspace/FacultyWorkspaceAssignments'));
const FacultyInterventionsPage = withSuspense(() => import('../pages/faculty/workspace/FacultyInterventionsPage'));
const ProjectUpload = withSuspense(() => import('../pages/student/ProjectUpload'));

const CodingAnalyticsOverview = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsOverview'),
);
const CodingAnalyticsStudentProgress = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsStudentProgress'),
);
const CodingAnalyticsBatchProgress = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsBatchProgress'),
);
const CodingAnalyticsFacultyEffectiveness = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsFacultyEffectiveness'),
);
const CodingAnalyticsPlatformComparison = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsPlatformComparison'),
);
const CodingAnalyticsBatchPerformance = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsBatchPerformance'),
);
const CodingAnalyticsLeetcode = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsLeetcode'),
);
const CodingAnalyticsCodeforces = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsCodeforces'),
);
const CodeforcesOverviewTab = lazy(
  () => import('../pages/faculty/coding-analytics/codeforces/CodeforcesOverviewTab'),
);
const CodeforcesStudentsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codeforces/CodeforcesStudentsTab'),
);
const CodeforcesContestsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codeforces/CodeforcesContestsTab'),
);
const CodeforcesRatingsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codeforces/CodeforcesRatingsTab'),
);
const CodeforcesTagsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codeforces/CodeforcesTagsTab'),
);
const CodeforcesTrendsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codeforces/CodeforcesTrendsTab'),
);
const LeetcodeOverviewTab = lazy(
  () => import('../pages/faculty/coding-analytics/leetcode/LeetcodeOverviewTab'),
);
const LeetcodeStudentsTab = lazy(
  () => import('../pages/faculty/coding-analytics/leetcode/LeetcodeStudentsTab'),
);
const LeetcodeTrendsTab = lazy(
  () => import('../pages/faculty/coding-analytics/leetcode/LeetcodeTrendsTab'),
);
const LeetcodeRatingsTab = lazy(
  () => import('../pages/faculty/coding-analytics/leetcode/LeetcodeRatingsTab'),
);
const LeetcodeTagsTab = lazy(
  () => import('../pages/faculty/coding-analytics/leetcode/LeetcodeTagsTab'),
);
const LeetcodeContestsTab = lazy(
  () => import('../pages/faculty/coding-analytics/leetcode/LeetcodeContestsTab'),
);
const CodingAnalyticsGeeksforgeeks = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsGeeksforgeeks'),
);
const CodingAnalyticsCodechef = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsCodechef'),
);
const CodeChefOverviewTab = lazy(
  () => import('../pages/faculty/coding-analytics/codechef/CodeChefOverviewTab'),
);
const CodeChefStudentsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codechef/CodeChefStudentsTab'),
);
const CodeChefContestsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codechef/CodeChefContestsTab'),
);
const CodeChefRatingsTab = lazy(
  () => import('../pages/faculty/coding-analytics/codechef/CodeChefRatingsTab'),
);
const CodeChefActivityTab = lazy(
  () => import('../pages/faculty/coding-analytics/codechef/CodeChefActivityTab'),
);
const CodingAnalyticsContests = lazy(
  () => import('../pages/faculty/coding-analytics/CodingAnalyticsContests'),
);
const ContestOverviewTab = lazy(
  () => import('../pages/faculty/coding-analytics/contests/ContestOverviewTab'),
);
const ContestContestsTab = lazy(
  () => import('../pages/faculty/coding-analytics/contests/ContestContestsTab'),
);
const AdminAnalyticsShell = lazy(
  () => import('../components/admin/AdminAnalyticsShell'),
);
const AdminAnalyticsOverviewTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsOverviewTab'),
);
const AdminAnalyticsCohortsTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsCohortsTab'),
);
const AdminAnalyticsBranchesTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsBranchesTab'),
);
const AdminAnalyticsFacultyImpactTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsFacultyImpactTab'),
);
const AdminAnalyticsPlatformEngagementTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsPlatformEngagementTab'),
);
const AdminAnalyticsExtensionAdoptionTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsExtensionAdoptionTab'),
);
const AdminAnalyticsContestCoverageTab = lazy(
  () => import('../pages/admin/analytics/AdminAnalyticsContestCoverageTab'),
);

const ReportingIntelligenceShell = withSuspense(
  () => import('../components/reporting-intelligence/ReportingIntelligenceShell'),
);
const ReportingIntelligenceLandingPage = lazy(
  () => import('../pages/reporting-intelligence/ReportingIntelligenceLandingPage'),
);
const GenerateReportPage = lazy(
  () => import('../pages/reporting-intelligence/GenerateReportPage'),
);
const ExecutiveBriefingsPage = lazy(
  () => import('../pages/reporting-intelligence/ExecutiveBriefingsPage'),
);
const ReportLibraryPage = lazy(
  () => import('../pages/reporting-intelligence/ReportLibraryPage'),
);
const ReportPreviewPage = lazy(
  () => import('../pages/reporting-intelligence/ReportPreviewPage'),
);
const CodingIntelligencePage = lazy(
  () => import('../pages/reporting-intelligence/CodingIntelligencePage'),
);
const LearningIntelligencePage = lazy(
  () => import('../pages/reporting-intelligence/LearningIntelligencePage'),
);
const Student360Page = lazy(
  () => import('../pages/reporting-intelligence/Student360Page'),
);
const PlacementByodPage = lazy(
  () => import('../pages/reporting-intelligence/PlacementByodPage'),
);
const InternshipByodPage = lazy(
  () => import('../pages/reporting-intelligence/InternshipByodPage'),
);
const DepartmentExecutivePage = lazy(
  () => import('../pages/reporting-intelligence/DepartmentExecutivePage'),
);
const InstitutionExecutivePage = lazy(
  () => import('../pages/reporting-intelligence/InstitutionExecutivePage'),
);

function reportingIntelligenceRoutes() {
  return (
    <Route
      path="reporting-intelligence"
      element={
        <RequireReportingIntelligence>
          <ReportingIntelligenceShell />
        </RequireReportingIntelligence>
      }
    >
      <Route
        index
        element={(
          <Suspense fallback={<RouteFallback />}>
            <ReportingIntelligenceLandingPage />
          </Suspense>
        )}
      />
      {/* Legacy paths */}
      <Route path="executive" element={<Navigate to=".." replace relative="path" />} />
      <Route path="overview" element={<Navigate to=".." replace relative="path" />} />
      <Route path="library" element={<Navigate to="../reports" replace relative="path" />} />
      <Route path="preview" element={<Navigate to="../view" replace relative="path" />} />
      <Route path="department-executive" element={<Navigate to="../department" replace relative="path" />} />
      <Route path="institution-executive" element={<Navigate to="../institution" replace relative="path" />} />
      <Route path="placement-byod" element={<Navigate to="../placements" replace relative="path" />} />
      <Route path="internship-byod" element={<Navigate to="../internships" replace relative="path" />} />
      <Route
        path="generate"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <GenerateReportPage />
          </Suspense>
        )}
      />
      <Route
        path="briefings"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <ExecutiveBriefingsPage />
          </Suspense>
        )}
      />
      <Route
        path="reports"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <ReportLibraryPage />
          </Suspense>
        )}
      />
      <Route
        path="view"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <ReportPreviewPage />
          </Suspense>
        )}
      />
      <Route
        path="coding"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <CodingIntelligencePage />
          </Suspense>
        )}
      />
      <Route
        path="learning"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <LearningIntelligencePage />
          </Suspense>
        )}
      />
      <Route
        path="student-360"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <Student360Page />
          </Suspense>
        )}
      />
      <Route
        path="placements"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <PlacementByodPage />
          </Suspense>
        )}
      />
      <Route
        path="internships"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <InternshipByodPage />
          </Suspense>
        )}
      />
      <Route
        path="department"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <DepartmentExecutivePage />
          </Suspense>
        )}
      />
      <Route
        path="institution"
        element={(
          <Suspense fallback={<RouteFallback />}>
            <InstitutionExecutivePage />
          </Suspense>
        )}
      />
    </Route>
  );
}

function CodingAnalyticsRouteFallback() {
  return <RouteFallback />;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

/** Faculty area index: subtype-aware default (see {@link getDefaultRoute}). */
function FacultyIndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={getDefaultRoute(user?.role, user)} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/unauthorized" element={<AccessDenied />} />

      {/* Public legal documents — deliberately outside RequireAuth. Paths carry no
          `/stacktrack` prefix; the router basename adds it (AD-LP-1). */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/account-linking" element={<AccountLinkingPage />} />

      <Route path="/projects" element={<RoleBasedProjectRedirect />} />

      {/* Legacy / ambiguous URLs → role-scoped routes */}
      <Route path="/dashboard" element={<LegacyRoleHomeRedirect />} />
      <Route path="/profile" element={<LegacyProfileRedirect />} />
      <Route path="/courses/*" element={<LegacyCoursesPathRedirect />} />
      <Route path="/upload" element={<Navigate to="/student/upload" replace />} />
      <Route path="/assignments" element={<Navigate to="/student/assignments" replace />} />
      <Route path="/coding-tracker" element={<LegacyCodingTrackerRedirect />} />
      <Route path="/coding/overview" element={<LegacyCodingOverviewRedirect />} />
      <Route path="/coding/activity" element={<LegacyCodingActivityRedirect />} />
      <Route path="/plagiarism-analytics" element={<LegacyPlagiarismRedirect />} />
      <Route path="/cbc/placement/intelligence" element={<Navigate to="/faculty/placement/preparation" replace />} />
      <Route path="/cbc/company-intelligence" element={<Navigate to="/faculty/company-intelligence" replace />} />
      <Route path="/project/:id" element={<LegacyProjectDetailRedirect />} />
      <Route path="/project/:id/evaluate" element={<LegacyProjectEvaluateRedirect />} />
      <Route path="/batch/*" element={<LegacyBatchRedirect />} />

      {/* HOD (Head of Department) — read-only oversight. Admin retained for superset view. */}
      <Route
        path="/hod"
        element={
          <RequireAuth roles={['hod', 'admin']}>
            <HodLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<HodDashboard />} />
        <Route path="intervention" element={<HodInterventionDashboard />} />
        <Route path="students" element={<HodStudents />} />
        <Route path="students/:studentId" element={<HodStudent360 />} />
        <Route path="batches" element={<HodBatches />} />
        <Route path="batches/:batchId/coding" element={<HodBatchCoding />} />
        <Route path="faculty" element={<HodFaculty />} />
        <Route path="mock-interviews" element={<HodMockInterviews />} />
        <Route path="diary-metrics" element={<HodDiaryMetrics />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      <Route
        path="/admin"
        element={
          <RequireAuth roles={['admin']}>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<AdminOverview />} />
        <Route path="profile" element={<Profile />} />
        <Route path="courses-overview" element={<UnderConstruction />} />
        <Route path="teaching-reports" element={<AdminTeachingReportsPage />} />
        {/* Feature-gated server-side: the API answers 404 when the flag is off,
            and 403 without TRANSFER_DIARY_OWNERSHIP. The route stays mounted so
            a bookmarked URL reports why rather than silently 404-ing. */}
        <Route path="faculty-diary/transfer" element={<DiaryTransferPage />} />
        <Route
          path="problem-library"
          element={
            <RequireProblemIntelligenceRead>
              <ProblemLibraryListPage />
            </RequireProblemIntelligenceRead>
          }
        />
        <Route
          path="problem-library/analytics"
          element={
            <RequireProblemIntelligenceRead>
              <ProblemLibraryAnalyticsPage />
            </RequireProblemIntelligenceRead>
          }
        />
        <Route
          path="problem-library/:referenceKey"
          element={
            <RequireProblemIntelligenceRead>
              <ProblemLibraryDetailPage />
            </RequireProblemIntelligenceRead>
          }
        />
        <Route path="lab-activity" element={<UnderConstruction />} />
        <Route path="platform-analytics" element={<PlatformHealthPage />} />
        <Route path="platform-health" element={<PlatformHealthPage />} />
        <Route path="sync-operations" element={<SyncOperationsPage />} />
        <Route path="data-integrity" element={<DataIntegrityPage />} />
        <Route path="student-recovery" element={<StudentRecoveryPage />} />
        <Route path="backups" element={<AdminBackupsPage />} />
        <Route path="features" element={<FeatureManagementPage />} />
        <Route path="monitoring-rules" element={<MonitoringRulesPage />} />
        <Route path="assessment-anomalies" element={<AssessmentAnomaliesPage />} />
        <Route path="monitoring-alarms" element={<MonitoringAlarmsPage />} />
        <Route path="monitoring-analytics" element={<MonitoringAnalyticsPage />} />
        <Route path="monitoring-purge" element={<MonitoringPurgePage />} />
        <Route path="problem-intelligence" element={<ProblemIntelligenceOpsPage />} />
        <Route path="placement/overview" element={<RequirePlacementPermission check={canViewPlacementOverview}><PlacementOverviewPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><FacultyPreparationWorkspacePage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/campaigns" element={<RequirePlacementPermission check={canManageMockCampaigns}><PlacementDrivesPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/campaigns/:campaignId/capture" element={<RequirePlacementPermission check={canCurateCompanyIntel}><PostDriveCapturePage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/campaigns/:campaignId" element={<RequirePlacementPermission check={canManageMockCampaigns}><PlacementDriveDetailPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/students" element={<RequirePlacementPermission check={canViewCoachingStudents}><FacultyAttentionPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/insights" element={<RequirePlacementPermission check={canViewCoachingInsights}><CoordinatorCareerOverviewPage /></RequirePlacementPermission>} />
        <Route path="placement/intelligence" element={<Navigate to="/admin/placement/preparation" replace />} />
        <Route path="placement/batches/:batchId/readiness" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><BatchPlacementReadinessPage /></RequirePlacementPermission>} />
        <Route path="placement/journey/today" element={<Navigate to="/admin/placement/preparation" replace />} />
        <Route path="placement/journey/batch/:batchId" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><FacultyJourneyBatchPage /></RequirePlacementPermission>} />
        <Route path="placement/journey/batch/:batchId/assignment/:assignmentId/monitor" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><FacultyJourneyMonitorPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/interviews" element={<RequirePlacementPermission check={canConductMockInterview}><AssignedInterviewsPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/interviews/:sessionId" element={<RequirePlacementPermission check={canConductMockInterview}><InterviewScreen /></RequirePlacementPermission>} />
        <Route path="company-intelligence" element={<RequirePlacementPermission check={canCurateCompanyIntel}><CompanyIntelligenceAdminPage /></RequirePlacementPermission>} />
        <Route path="placement/campaigns" element={<Navigate to="/admin/placement/preparation/campaigns" replace />} />
        <Route path="placement/campaigns/:campaignId" element={<LegacyPlacementCampaignRedirect />} />
        <Route path="placement/students" element={<Navigate to="/admin/placement/preparation/students" replace />} />
        <Route path="placement/reports" element={<Navigate to="/admin/placement/preparation/insights" replace />} />
        <Route path="placement/interviews" element={<Navigate to="/admin/placement/preparation/interviews" replace />} />
        <Route path="placement/interviews/:sessionId" element={<LegacyPlacementInterviewRedirect />} />
        <Route path="placement/drives" element={<Navigate to="/admin/placement/preparation/campaigns" replace />} />
        <Route path="placement/drives/:driveId" element={<LegacyPlacementDriveRedirect />} />
        <Route path="placement/attention" element={<Navigate to="/admin/placement/preparation/students" replace />} />
        <Route path="placement/insights" element={<Navigate to="/admin/placement/preparation/insights" replace />} />
        <Route path="recovery-center" element={<RecoveryCenterPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="access-control" element={<AccessControlPage />} />
        <Route path="coding-platforms" element={<CodingPlatformsPage />} />
        <Route path="platform-handle-claims" element={<PlatformHandleClaimsPage />} />
        <Route path="plagiarism-analytics" element={<PlagiarismAnalyticsPage />} />
        <Route path="coding-tracker" element={<BatchListPage />} />
        <Route path="batch/:batchId" element={<BatchDetailsPage />} />
        <Route path="batch/:batchId/assignments" element={<AssignmentPage />} />
        <Route path="batch/:batchId/assignments/:assignmentId" element={<AssignmentDetailsPage />} />
        <Route path="batch/:batchId/assignments/:assignmentId/progress" element={<ProgressPage />} />
        <Route path="batch/:batchId/students" element={<BatchStudentsTabRedirect />} />
        <Route
          path="analytics"
          element={
            <Suspense fallback={<CodingAnalyticsRouteFallback />}>
              <AdminAnalyticsShell />
            </Suspense>
          }
        >
          <Route index element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsOverviewTab /></Suspense>} />
          <Route path="cohorts" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsCohortsTab /></Suspense>} />
          <Route path="branches" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsBranchesTab /></Suspense>} />
          <Route path="faculty-impact" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsFacultyImpactTab /></Suspense>} />
          <Route path="platform-engagement" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsPlatformEngagementTab /></Suspense>} />
          <Route path="contest-coverage" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsContestCoverageTab /></Suspense>} />
          <Route path="extension-adoption" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><AdminAnalyticsExtensionAdoptionTab /></Suspense>} />
        </Route>
        {reportingIntelligenceRoutes()}
      </Route>

      <Route
        path="/faculty"
        element={
          <RequireAuth roles={['faculty']}>
            <FacultyLayout />
          </RequireAuth>
        }
      >
        <Route index element={<FacultyIndexRedirect />} />
        <Route path="profile" element={<Profile />} />

        <Route path="diary" element={<RequireFacultyTeaching><DiaryDashboardPage /></RequireFacultyTeaching>} />
        <Route path="diary/day" element={<RequireFacultyTeaching><DiaryDayPage /></RequireFacultyTeaching>} />
        <Route path="diary/day/:dayKey" element={<RequireFacultyTeaching><DiaryDayPage /></RequireFacultyTeaching>} />
        <Route path="diary/compose" element={<RequireFacultyTeaching><DiaryComposePage /></RequireFacultyTeaching>} />
        <Route path="diary/entries/:id" element={<RequireFacultyTeaching><DiaryEntryDetailPage /></RequireFacultyTeaching>} />
        <Route path="diary/entries/:id/edit" element={<RequireFacultyTeaching><DiaryComposePage /></RequireFacultyTeaching>} />

        <Route path="lesson-plans" element={<RequireFacultyTeaching><LessonPlanListPage /></RequireFacultyTeaching>} />
        <Route path="lesson-plans/new" element={<RequireFacultyTeaching><LessonPlanFormPage /></RequireFacultyTeaching>} />
        <Route path="lesson-plans/import" element={<RequireFacultyTeaching><LessonPlanImportPage /></RequireFacultyTeaching>} />
        <Route path="lesson-plans/:planId" element={<RequireFacultyTeaching><LessonPlanDetailPage /></RequireFacultyTeaching>} />
        <Route path="lesson-plans/:planId/edit" element={<RequireFacultyTeaching><LessonPlanFormPage /></RequireFacultyTeaching>} />
        <Route path="lesson-plans/:planId/sessions" element={<RequireFacultyTeaching><LessonPlanSessionsPage /></RequireFacultyTeaching>} />
        <Route path="teaching-reports" element={<RequireFacultyTeaching><TeachingReportsPage /></RequireFacultyTeaching>} />
        <Route path="session-library" element={<RequireFacultyTeaching><SessionLibraryPage /></RequireFacultyTeaching>} />
        <Route path="session-library/new" element={<RequireFacultyTeaching><SessionTemplateFormPage /></RequireFacultyTeaching>} />
        <Route path="session-library/:templateId" element={<RequireFacultyTeaching><SessionTemplateDetailPage /></RequireFacultyTeaching>} />
        <Route path="session-library/:templateId/edit" element={<RequireFacultyTeaching><SessionTemplateFormPage /></RequireFacultyTeaching>} />

        <Route
          path="problem-library"
          element={
            <RequireFacultyCoding>
              <RequireProblemIntelligenceRead>
                <ProblemLibraryListPage />
              </RequireProblemIntelligenceRead>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="problem-library/analytics"
          element={
            <RequireFacultyCoding>
              <RequireProblemIntelligenceRead>
                <ProblemLibraryAnalyticsPage />
              </RequireProblemIntelligenceRead>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="problem-library/:referenceKey"
          element={
            <RequireFacultyCoding>
              <RequireProblemIntelligenceRead>
                <ProblemLibraryDetailPage />
              </RequireProblemIntelligenceRead>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <AssessmentListPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/new"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <AssessmentWizardPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        {/* Results-first detail page. Static `new` still wins over this
            dynamic segment, so authoring is unaffected. */}
        <Route
          path="assessments/:assessmentId"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <AssessmentDetailPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/edit"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <AssessmentWizardPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/monitor"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <LiveMonitorPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/summary"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <AssessmentSummaryPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/review"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <ReviewQueuePage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/integrity-dashboard"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <IntegrityDashboardPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/attempts/:attemptId/integrity-report"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <IntegrityReportPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />
        <Route
          path="assessments/:assessmentId/attempts/:attemptId"
          element={
            <RequireFacultyCoding>
              <RequireAssessmentOrchestration>
                <AttemptDetailPage />
              </RequireAssessmentOrchestration>
            </RequireFacultyCoding>
          }
        />

        <Route path="dashboard" element={<RequireFacultyTeaching><FacultyDashboard /></RequireFacultyTeaching>} />
        <Route path="placement/overview" element={<RequirePlacementPermission check={canViewPlacementOverview}><PlacementOverviewPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><FacultyPreparationWorkspacePage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/campaigns" element={<RequirePlacementPermission check={canManageMockCampaigns}><PlacementDrivesPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/campaigns/:campaignId/capture" element={<RequirePlacementPermission check={canCurateCompanyIntel}><PostDriveCapturePage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/campaigns/:campaignId" element={<RequirePlacementPermission check={canManageMockCampaigns}><PlacementDriveDetailPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/students" element={<RequirePlacementPermission check={canViewCoachingStudents}><FacultyAttentionPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/insights" element={<RequirePlacementPermission check={canViewCoachingInsights}><CoordinatorCareerOverviewPage /></RequirePlacementPermission>} />
        <Route path="placement/intelligence" element={<Navigate to="/faculty/placement/preparation" replace />} />
        <Route path="placement/batches/:batchId/readiness" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><BatchPlacementReadinessPage /></RequirePlacementPermission>} />
        <Route path="placement/journey/today" element={<Navigate to="/faculty/placement/preparation" replace />} />
        <Route path="placement/journey/batch/:batchId" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><FacultyJourneyBatchPage /></RequirePlacementPermission>} />
        <Route path="placement/journey/batch/:batchId/assignment/:assignmentId/monitor" element={<RequirePlacementPermission check={canViewPlacementBatchAnalytics}><FacultyJourneyMonitorPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/interviews" element={<RequirePlacementPermission check={canConductMockInterview}><AssignedInterviewsPage /></RequirePlacementPermission>} />
        <Route path="placement/preparation/interviews/:sessionId" element={<RequirePlacementPermission check={canConductMockInterview}><InterviewScreen /></RequirePlacementPermission>} />
        <Route path="company-intelligence" element={<RequirePlacementPermission check={canCurateCompanyIntel}><CompanyIntelligenceAdminPage /></RequirePlacementPermission>} />
        <Route path="placement/campaigns" element={<Navigate to="/faculty/placement/preparation/campaigns" replace />} />
        <Route path="placement/campaigns/:campaignId" element={<LegacyPlacementCampaignRedirect />} />
        <Route path="placement/students" element={<Navigate to="/faculty/placement/preparation/students" replace />} />
        <Route path="placement/reports" element={<Navigate to="/faculty/placement/preparation/insights" replace />} />
        <Route path="placement/interviews" element={<Navigate to="/faculty/placement/preparation/interviews" replace />} />
        <Route path="placement/interviews/:sessionId" element={<LegacyPlacementInterviewRedirect />} />
        <Route path="placement/drives" element={<Navigate to="/faculty/placement/preparation/campaigns" replace />} />
        <Route path="placement/drives/:driveId" element={<LegacyPlacementDriveRedirect />} />
        <Route path="placement/attention" element={<Navigate to="/faculty/placement/preparation/students" replace />} />
        <Route path="placement/insights" element={<Navigate to="/faculty/placement/preparation/insights" replace />} />
        <Route path="grading" element={<Navigate to="/faculty/dashboard" replace />} />
        <Route path="students" element={<RequireFacultyTeaching><FacultyStudentsPage /></RequireFacultyTeaching>} />
        <Route path="projects" element={<ProtectedRoute allowedRoles={['faculty']}><FacultyProjectDashboard /></ProtectedRoute>} />
        <Route path="projects-dashboard" element={<Navigate to="/faculty/projects" replace />} />
        <Route path="courses" element={<RequireFacultyTeaching><MyCourses /></RequireFacultyTeaching>} />
        <Route path="lab-internals" element={<RequireFacultyTeaching><FacultyLabInternalsPage /></RequireFacultyTeaching>} />
        <Route path="lab-internals/new" element={<RequireFacultyTeaching><CreateLabInternalPage /></RequireFacultyTeaching>} />
        <Route path="lab-internals/:labId/edit" element={<RequireFacultyTeaching><CreateLabInternalPage /></RequireFacultyTeaching>} />
        <Route path="lab-internals/:labId/evaluate" element={<RequireFacultyTeaching><FacultyStudentEvaluationPage /></RequireFacultyTeaching>} />
        <Route
          path="lab-internals/:labId/student/:studentLabId"
          element={<RequireFacultyTeaching><FacultyStudentEvaluationPage /></RequireFacultyTeaching>}
        />
        <Route path="lab-internals/:labId" element={<RequireFacultyTeaching><FacultyLabDetailPage /></RequireFacultyTeaching>} />
        <Route path="questions" element={<RequireFacultyTeaching><FacultyQuestionBankPage /></RequireFacultyTeaching>} />
        <Route path="questions/new" element={<RequireFacultyTeaching><QuestionFormPage /></RequireFacultyTeaching>} />
        <Route path="questions/:id/edit" element={<RequireFacultyTeaching><QuestionFormPage /></RequireFacultyTeaching>} />
        <Route path="project/:id" element={<RequireFacultyTeaching><ProjectDetails /></RequireFacultyTeaching>} />
        <Route path="project/:id/evaluate" element={<RequireFacultyTeaching><ProjectEvaluation /></RequireFacultyTeaching>} />
        <Route path="lab-results" element={<RequireFacultyTeaching><FacultyLabResultsPage /></RequireFacultyTeaching>} />
        <Route
          path="lab-results/:submissionId"
          element={<RequireFacultyTeaching><FacultyLabSubmissionDetailPage /></RequireFacultyTeaching>}
        />

        <Route path="coding-dashboard" element={<Navigate to="/faculty/workspace/batches" replace />} />
        <Route path="coding-tracker" element={<Navigate to="/faculty/workspace/batches" replace />} />
        <Route path="plagiarism-analytics" element={<RequireCbcFaculty><PlagiarismAnalyticsPage /></RequireCbcFaculty>} />
        <Route path="activity-feed" element={<RequireCbcFaculty><FacultyActivityFeedPage /></RequireCbcFaculty>} />
        <Route path="coding/overview" element={<Navigate to="/faculty/workspace/overview" replace />} />
        <Route path="coding/activity" element={<Navigate to="/faculty/activity-feed" replace />} />
        <Route path="coding-analytics/*" element={<LegacyFacultyCodingAnalyticsRedirect />} />
        <Route
          path="workspace"
          element={
            <RequireFacultyCoding>
              <CodingWorkspaceShell />
            </RequireFacultyCoding>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<FacultyWorkspaceOverview />} />
          <Route path="batches" element={<FacultyWorkspaceBatchesPage />} />
          <Route path="assignments" element={<FacultyWorkspaceAssignments />} />
          <Route path="interventions" element={<FacultyInterventionsPage />} />
        </Route>
        {reportingIntelligenceRoutes()}
        <Route
          path="analytics"
          element={
            <RequireFacultyCoding>
              <CodingAnalyticsShell />
            </RequireFacultyCoding>
          }
        >
          <Route index element={<Navigate to="overall" replace />} />
          <Route
            path="overall"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsOverview />
              </Suspense>
            }
          />
          <Route
            path="students"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsStudentProgress />
              </Suspense>
            }
          />
          <Route
            path="batches"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsBatchProgress />
              </Suspense>
            }
          />
          <Route
            path="effectiveness"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsFacultyEffectiveness />
              </Suspense>
            }
          />
          <Route path="platforms" element={<Navigate to="/faculty/analytics/platforms/comparison" replace />} />
          <Route
            path="platforms/comparison"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsPlatformComparison />
              </Suspense>
            }
          />
          <Route
            path="platforms/performance"
            element={
              // AD-3: this report reads /api/coding-tracker/*, so it is guarded
              // to exactly the roles that API accepts — otherwise a teaching
              // faculty user reaches the page and every request 403s.
              <RequirePlatformAnalyticsApi>
                <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                  <CodingAnalyticsBatchPerformance />
                </Suspense>
              </RequirePlatformAnalyticsApi>
            }
          />
          <Route
            path="platforms/leetcode"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsLeetcode />
              </Suspense>
            }
          >
            <Route index element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><LeetcodeOverviewTab /></Suspense>} />
            <Route path="students" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><LeetcodeStudentsTab /></Suspense>} />
            <Route path="trends" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><LeetcodeTrendsTab /></Suspense>} />
            <Route path="ratings" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><LeetcodeRatingsTab /></Suspense>} />
            <Route path="tags" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><LeetcodeTagsTab /></Suspense>} />
            <Route path="contests" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><LeetcodeContestsTab /></Suspense>} />
          </Route>
          <Route
            path="platforms/codeforces"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsCodeforces />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                  <CodeforcesOverviewTab />
                </Suspense>
              }
            />
            <Route path="students" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeforcesStudentsTab /></Suspense>} />
            <Route path="contests" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeforcesContestsTab /></Suspense>} />
            <Route path="ratings" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeforcesRatingsTab /></Suspense>} />
            <Route path="tags" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeforcesTagsTab /></Suspense>} />
            <Route path="trends" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeforcesTrendsTab /></Suspense>} />
          </Route>
          <Route
            path="platforms/geeksforgeeks"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsGeeksforgeeks />
              </Suspense>
            }
          />
          <Route
            path="platforms/codechef"
            element={
              <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                <CodingAnalyticsCodechef />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                  <CodeChefOverviewTab />
                </Suspense>
              }
            />
            <Route path="students" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeChefStudentsTab /></Suspense>} />
            <Route path="contests" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeChefContestsTab /></Suspense>} />
            <Route path="ratings" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeChefRatingsTab /></Suspense>} />
            <Route path="activity" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><CodeChefActivityTab /></Suspense>} />
          </Route>
          <Route
            path="platforms/contests"
            element={
              <RequireCbcFaculty>
                <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                  <CodingAnalyticsContests />
                </Suspense>
              </RequireCbcFaculty>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<CodingAnalyticsRouteFallback />}>
                  <ContestOverviewTab />
                </Suspense>
              }
            />
            <Route path="contests" element={<Suspense fallback={<CodingAnalyticsRouteFallback />}><ContestContestsTab /></Suspense>} />
          </Route>
          {/* Legacy flat platform paths → nested Platform Insights routes */}
          <Route path="leetcode" element={<Navigate to="platforms/leetcode" replace />} />
          <Route path="codeforces" element={<Navigate to="platforms/codeforces" replace />} />
          <Route path="geeksforgeeks" element={<Navigate to="platforms/geeksforgeeks" replace />} />
          <Route path="codechef" element={<Navigate to="platforms/codechef" replace />} />
        </Route>
        <Route path="batch/:batchId" element={<RequireFacultyCoding><BatchDetailsPage /></RequireFacultyCoding>} />
        <Route path="batch/:batchId/assignments" element={<RequireFacultyCoding><AssignmentPage /></RequireFacultyCoding>} />
        <Route path="batch/:batchId/assignments/:assignmentId" element={<RequireFacultyCoding><AssignmentDetailsPage /></RequireFacultyCoding>} />
        <Route path="batch/:batchId/assignments/:assignmentId/progress" element={<RequireFacultyCoding><ProgressPage /></RequireFacultyCoding>} />
        <Route path="batch/:batchId/students" element={<RequireFacultyCoding><BatchStudentsTabRedirect /></RequireFacultyCoding>} />
        <Route path="student/:studentId" element={<RequireFacultyTeaching><FacultyStudentPortfolioPage /></RequireFacultyTeaching>} />
      </Route>

      <Route
        path="/student"
        element={
          <RequireAuth roles={['student']}>
            <StudentLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<StudentDashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="dashboard" element={<StudentDashboard />} />
        {/* <Route path="dashboard/coding" element={<StudentCodingDashboard />} /> */}
        <Route path="projects" element={<RequireStudentEligibility feature="projects"><ProtectedRoute allowedRoles={['student']}><StudentProjectDashboard /></ProtectedRoute></RequireStudentEligibility>} />
        <Route path="projects-dashboard" element={<Navigate to="/student/projects" replace />} />
        <Route path="placement" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentPlacementDashboard /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/companies" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentCompanyMatchesPage /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/company/:companyId" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentCompanyInsightPage /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/hub" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentPlacementHubPage /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/profile" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentPlacementProfilePage /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/preparation/interviews" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentInterviewHistoryPage /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/preparation/insights" element={<RequireStudentEligibility feature="placementHub"><RequirePlacementPermission check={isStudentPlacementUser}><StudentCareerIntelligencePage /></RequirePlacementPermission></RequireStudentEligibility>} />
        <Route path="placement/interviews" element={<Navigate to="/student/placement/preparation/interviews" replace />} />
        <Route path="placement/feedback" element={<Navigate to="/student/placement/preparation/interviews" replace />} />
        <Route path="placement/insights" element={<Navigate to="/student/placement/preparation/insights" replace />} />
        <Route path="courses" element={<UnderConstruction />} />
        <Route path="assignments" element={<StudentAssignmentsDashboard />} />
        <Route path="assignments/:assignmentId" element={<StudentAssignmentDetailPage />} />
        <Route
          path="assessments"
          element={
            <RequireAssessmentOrchestration>
              <StudentAssessmentListPage />
            </RequireAssessmentOrchestration>
          }
        />
        <Route
          path="assessments/:assessmentId"
          element={
            <RequireAssessmentOrchestration>
              <AssessmentIntroPage />
            </RequireAssessmentOrchestration>
          }
        />
        {/* AO-3.1: live surface mounts as sibling outside this shell (ADR-AO-021). */}
        <Route
          path="assessments/:assessmentId/complete"
          element={
            <RequireAssessmentOrchestration>
              <AssessmentCompletePage />
            </RequireAssessmentOrchestration>
          }
        />
        <Route
          path="assessments/:assessmentId/results"
          element={
            <RequireAssessmentOrchestration>
              <AssessmentResultsPage />
            </RequireAssessmentOrchestration>
          }
        />
        <Route path='upload' element={<ProtectedRoute allowedRoles={['student']}><ProjectUpload /></ProtectedRoute>} />
        <Route path="lab-internals" element={<StudentLabDashboard />} />
        <Route path="lab-results" element={<StudentLabResultsPage />} />
        <Route path="lab-results/:submissionId" element={<StudentLabResultDetailPage />} />
        <Route path="results/:submissionId" element={<StudentSubmissionResultPage />} />
        <Route path="coding" element={<StudentCodingDashboard />} />
        <Route path="coding/progress" element={<StudentProgressPage />} />
        <Route path="progress" element={<StudentProgressPage />} />
        {process.env.NODE_ENV === 'development' ? (
          <Route path="progress/error-states" element={<PlatformProgressErrorShowcase />} />
        ) : null}
        <Route path="ide/:labInternalId/:questionId" element={<StudentIDE />} />
        <Route path="project/:id" element={<RequireStudentEligibility feature="projects"><ProjectDetails /></RequireStudentEligibility>} />
      </Route>

      {/* AO-3.1 — Assessment Container sibling surface (ADR-AO-021). Outside StudentLayout. */}
      <Route
        path="/student/assessments/:assessmentId/live"
        element={
          <RequireAuth roles={['student']}>
            <RequireAssessmentOrchestration>
              <AssessmentContainerLayout>
                <AssessmentLivePage />
              </AssessmentContainerLayout>
            </RequireAssessmentOrchestration>
          </RequireAuth>
        }
      />

      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );  
}
